import { QRCodeCanvas } from "qrcode.react";
import Barcode from "react-barcode";
import { currencyLabel, currencySymbol } from "./studioUtils";

const money = (n, currency = "₺") =>
  `${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

const STATUS_LABEL = {
  taslak: "Taslak", gonderildi: "Gönderildi", gorusuluyor: "Görüşülüyor",
  onaylandi: "Onaylandı", reddedildi: "Reddedildi",
};

export default function PrintSheet({ quote, company, rate, totals, vat, refCode, createdAt, publicUrl, ean13, showImages, showCustomerSign = false, showSecondPage = true }) {
  const label = currencyLabel(quote.currency);
  const symbol = currencySymbol(quote.currency);
  const displayRef = refCode || `${company.ref_prefix || "KNX"}-${new Date().toISOString().slice(0, 10).replaceAll("-", "")}`;
  const displayDate = createdAt ? new Date(createdAt).toLocaleDateString("tr-TR") : new Date().toLocaleDateString("tr-TR");
  const hasSecondPage = showSecondPage && ((company.references && company.references.length) || (company.partners && company.partners.length) || company.references_text || company.partners_text);
  const status = quote.status ? STATUS_LABEL[quote.status] : null;

  return (
    <div className="print-sheet" data-testid="print-sheet">
      <div className="print-page">
        <div className="print-accent" />
        <div className="print-header">
          <div className="print-brand">
            {company.logo_url && <img src={company.logo_url} alt="logo" className="print-logo" />}
            <div className="print-brand-text">
              <h1>{company.company_name || "CDS Teknoloji"}</h1>
              <div className="print-contact">
                {company.address && <p>{company.address}</p>}
                <p>
                  {company.phone && <span>{company.phone}</span>}
                  {company.email && <span>{company.email}</span>}
                  {company.website && <span>{company.website}</span>}
                </p>
                {(company.tax_office || company.tax_number) && (
                  <p className="print-vd"><b>V.D:</b> {company.tax_office}{company.tax_number && ` · ${company.tax_number}`}</p>
                )}
              </div>
            </div>
          </div>
          <div className="print-doc">
            <div className="print-doc-badge">PROFORMA FATURA</div>
            <div className="print-doc-meta">
              <p><span>Tarih</span><b>{displayDate}</b></p>
              <p><span>Ref No</span><b>{displayRef}</b></p>
              {status && <p><span>Durum</span><b className="print-status-badge">{status}</b></p>}
            </div>
            <div className="print-codes">
              {ean13 && (
                <div className="print-code-item">
                  <Barcode value={ean13} format="EAN13" width={1.35} height={34} fontSize={9} displayValue={true} margin={0} />
                </div>
              )}
              {publicUrl && (
                <div className="print-code-item print-qr">
                  <QRCodeCanvas value={publicUrl} size={68} level="M" includeMargin={false} />
                  <small>Mobilden görüntüle</small>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="print-customer">
          <div>
            <span className="print-label">Sayın</span>
            <b>{quote.customer_name || "-"}</b>
            {quote.customer_contact && <em>Yetkili: {quote.customer_contact}</em>}
          </div>
          <div>
            <span className="print-label">Konu</span>
            <b>{quote.subject || "-"}</b>
            {(quote.customer_tax_office || quote.customer_tax_number) &&
              <em>V.D: {quote.customer_tax_office}{quote.customer_tax_number && ` · ${quote.customer_tax_number}`}</em>}
          </div>
          <div className="print-rate-cell">
            <span className="print-label">Kur</span>
            <b data-testid="print-applied-rate">{Number(rate).toFixed(2)} <small>₺/{label}</small></b>
          </div>
        </div>

        <table className="print-table">
          <thead>
            <tr>
              {showImages && <th style={{ width: 62 }}>Görsel</th>}
              <th>Ürün / Hizmet</th>
              <th>Miktar</th>
              <th>Birim {label}</th>
              <th>İskonto</th>
              <th>Tutar {label}</th>
              <th>Tutar ₺</th>
            </tr>
          </thead>
          <tbody>
            {quote.items.map((item, index) => {
              const total = Number(item.quantity || 0) * Number(item.list_price || 0) * (1 - Number(item.discount || 0) / 100);
              return (
                <tr key={index}>
                  {showImages && (
                    <td className="print-thumb">
                      {item.image_url && <img src={item.image_url} alt="" />}
                    </td>
                  )}
                  <td className="print-product-name">{item.product_name || "-"}{item.sku && <small style={{ display: "block", color: "#64748b", fontSize: 9 }}>SKU: {item.sku}</small>}</td>
                  <td>{item.quantity}</td>
                  <td>{money(item.list_price, symbol)}</td>
                  <td>%{item.discount || 0}</td>
                  <td>{money(total, symbol)}</td>
                  <td className="print-tl-cell">{money(total * rate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="print-bottom">
          <div className="print-terms">
            <div className="print-term-row"><span>Teslimat</span><b>{quote.delivery_terms || "-"}</b></div>
            <div className="print-term-row"><span>Ödeme</span><b>{quote.payment_terms || "-"}</b></div>
            <div className="print-term-row"><span>Geçerlilik</span><b>{quote.validity_days || 1} gün</b></div>
            {quote.notes && <p className="print-note">{quote.notes}</p>}
          </div>
          <div className="print-totals">
            {quote.currency !== "TRY" && <p><span>Ara toplam {label}</span><b>{money(totals.usd, symbol)}</b></p>}
            <p><span>Ara toplam TL</span><b>{money(totals.tl)}</b></p>
            <p><span>KDV %{quote.vat_rate}</span><b>{money(vat)}</b></p>
            <p className="print-grand"><span>GENEL TOPLAM</span><b>{money(totals.tl + vat)}</b></p>
          </div>
        </div>

        <div className="print-footer">
          {company.bank_details && (
            <div className="print-bank">
              <b>Banka Bilgilerimiz</b>
              <span>{company.bank_details}</span>
            </div>
          )}
          <div className="print-sign-row">
            {showCustomerSign && (
              <div className="print-sign print-sign-customer">
                <div className="print-sign-empty" />
                <span>Müşteri Onayı</span>
                <em>Ad Soyad · Kaşe / İmza</em>
              </div>
            )}
            <div className="print-sign">
              {company.signature_url ? (
                <img src={company.signature_url} alt="imza" className="print-sign-img" />
              ) : (
                <div className="print-sign-empty" />
              )}
              <span>{company.company_name || "CDS Teknoloji"}</span>
              <em>Kaşe / İmza</em>
            </div>
          </div>
        </div>
      </div>

      {hasSecondPage && (
        <div className="print-page">
          <div className="print-accent" />
          <div className="print-second-header">
            {company.logo_url && <img src={company.logo_url} alt="logo" className="print-logo" />}
            <div>
              <h1>{company.company_name || "CDS Teknoloji"}</h1>
              <p>Referanslar & Çözüm Ortakları</p>
            </div>
          </div>
          {((company.references && company.references.length > 0) || company.references_text) && (
            <section className="print-refs">
              <h2>Referanslarımız</h2>
              {company.references_text && <p className="print-refs-text">{company.references_text}</p>}
              <div className="print-refs-grid">
                {(company.references || []).map((r, i) => (
                  <div className="print-ref-card" key={i}>
                    {r.logo_url && <img src={r.logo_url} alt={r.name} />}
                    <b>{r.name}</b>
                    {r.note && <small>{r.note}</small>}
                  </div>
                ))}
              </div>
            </section>
          )}
          {((company.partners && company.partners.length > 0) || company.partners_text) && (
            <section className="print-refs">
              <h2>Çözüm Ortaklarımız</h2>
              {company.partners_text && <p className="print-refs-text">{company.partners_text}</p>}
              <div className="print-refs-grid">
                {(company.partners || []).map((r, i) => (
                  <div className="print-ref-card" key={i}>
                    {r.logo_url && <img src={r.logo_url} alt={r.name} />}
                    <b>{r.name}</b>
                    {r.note && <small>{r.note}</small>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
